import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useLiveCallsCount } from "@/hooks/useLiveCallsCount";
import { usePendingPaymentsCount } from "@/hooks/usePendingPaymentsCount";
import {
  SquaresFour, UsersThree, Storefront, Megaphone, ShareNetwork, WaveSine,
  Tag, ChartBar, ListDashes, Gear, Wallet, Phone, PhoneCall, CaretLeft, CaretDown,
  Brain, Broadcast, ListMagnifyingGlass, Receipt, CreditCard, Gauge,
  AddressBook, HardDrives, ChartLineUp, SlidersHorizontal,
} from "@phosphor-icons/react";

// Menus support two node types: a leaf { to, label, icon } and a collapsible
// group { group, icon, children:[leaf...] }.
const NAV = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: SquaresFour, end: true },
    { group: "Accounts", icon: AddressBook, children: [
      { to: "/customers", label: "Customers", icon: UsersThree },
      { to: "/resellers", label: "Resellers", icon: Storefront },
    ] },
    { to: "/templates", label: "Global Campaigns", icon: Megaphone },
    { to: "/live-calls", label: "Live Calls", icon: Broadcast },
    { group: "Servers", icon: HardDrives, children: [
      { to: "/servers/opensips", label: "OpenSIPS Servers", icon: ShareNetwork },
      { to: "/servers/media", label: "Media Servers", icon: WaveSine },
      { to: "/servers/ai-engines", label: "AI Engine Servers", icon: Brain },
    ] },
    { to: "/servers/vicidial", label: "VICIdial Servers", icon: PhoneCall },
    { group: "Billing", icon: Receipt, children: [
      { to: "/billing/pricing", label: "Billing", icon: Wallet },
      { to: "/billing/payments", label: "Payments", icon: CreditCard },
    ] },
    { group: "Reports", icon: ChartLineUp, children: [
      { to: "/cdr", label: "CDR", icon: ListDashes },
      { to: "/reports", label: "Report", icon: ChartBar, end: true },
      { to: "/reports/stats", label: "Stats Report", icon: Gauge },
    ] },
    { group: "System", icon: Gear, children: [
      { to: "/settings", label: "System Settings", icon: SlidersHorizontal },
      { to: "/system-logs", label: "System Logs", icon: ListMagnifyingGlass },
    ] },
  ],
  reseller: [
    { to: "/dashboard", label: "Dashboard", icon: SquaresFour, end: true },
    { to: "/customers", label: "Customers", icon: UsersThree },
    { to: "/live-calls", label: "Live Calls", icon: Broadcast },
    { to: "/call-limit", label: "Call Limit", icon: Gauge },
    { to: "/billing", label: "Billing", icon: Receipt },
    { to: "/payments", label: "Payments", icon: CreditCard },
    { to: "/reseller-pricing", label: "Pricing", icon: Tag },
    { to: "/cdr", label: "CDR", icon: ListDashes },
  ],
  customer: [
    { to: "/dashboard", label: "Dashboard", icon: SquaresFour, end: true },
    { to: "/servers/vicidial", label: "VICIdial Servers", icon: PhoneCall },
    { to: "/campaigns", label: "Campaigns", icon: Megaphone },
    { to: "/live-calls", label: "Live Calls", icon: Broadcast },
    { to: "/call-limit", label: "Call Limit", icon: Gauge },
    { group: "Billing", icon: Receipt, children: [
      { to: "/billing", label: "Billing", icon: Wallet },
      { to: "/payments", label: "Payments", icon: CreditCard },
      { to: "/pricing", label: "Pricing", icon: Tag },
    ] },
    { group: "Report", icon: ChartLineUp, children: [
      { to: "/cdr", label: "CDR", icon: ListDashes },
    ] },
  ],
};

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
const childActive = (children, pathname) =>
  children.some((c) => pathname === c.to || pathname.startsWith(c.to + "/"));

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const items = NAV[user?.role] || [];
  const role = user?.role;
  const liveCount = useLiveCallsCount();
  const pendingPayments = usePendingPaymentsCount();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});

  // Auto-expand the group that owns the active route.
  useEffect(() => {
    items.forEach((it) => {
      if (it.group && childActive(it.children, location.pathname)) {
        setOpenGroups((p) => (p[it.group] ? p : { ...p, [it.group]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.role]);

  const Leaf = ({ it, nested }) => (
    <NavLink
      to={it.to}
      end={it.end}
      data-testid={`nav-${slug(it.label)}`}
      className={({ isActive }) =>
        cn(
          "relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200",
          collapsed && "justify-center px-0",
          nested && !collapsed && "pl-9",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )
      }
    >
      <it.icon className="w-5 h-5 shrink-0" weight="duotone" />
      {!collapsed && <span className="truncate">{it.label}</span>}
      {it.to === "/live-calls" && liveCount > 0 && (
        collapsed ? (
          <span
            data-testid="live-calls-badge-dot"
            className="absolute top-1.5 right-2.5 h-2.5 w-2.5 rounded-full bg-[hsl(var(--success))] ring-2 ring-card"
          />
        ) : (
          <span
            data-testid="live-calls-badge"
            className="ml-auto inline-flex items-center gap-1 rounded-full bg-[hsl(var(--success)/0.15)] px-2 py-0.5 text-xs font-semibold text-[hsl(var(--success))]"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--success))]" />
            </span>
            {liveCount}
          </span>
        )
      )}
      {(it.to === "/payments" || it.to === "/billing/payments") && pendingPayments > 0 && (
        collapsed ? (
          <span
            data-testid="pending-payments-dot"
            className="absolute top-1.5 right-2.5 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-card"
          />
        ) : (
          <span
            data-testid="pending-payments-badge"
            className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-bold"
          >
            {pendingPayments}
          </span>
        )
      )}
    </NavLink>
  );

  return (
    <aside
      className={cn(
        "shrink-0 border-r border-border bg-card/60 backdrop-blur-sm flex flex-col transition-[width] duration-300",
        collapsed ? "w-20" : "w-64"
      )}
      style={{ transitionTimingFunction: "cubic-bezier(0.4,0,0.2,1)" }}
      data-testid="sidebar"
    >
      <div className={cn("h-16 flex items-center gap-3 px-5 border-b border-border", collapsed && "justify-center px-0")}>
        <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground grid place-items-center shrink-0">
          <Phone className="w-5 h-5" weight="fill" />
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <p className="font-display font-bold tracking-tight">WA Calls</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Control Plane</p>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {collapsed
          ? items.flatMap((it) =>
              it.group
                ? it.children.map((c) => <Leaf key={c.to} it={c} />)
                : [<Leaf key={it.to} it={it} />]
            )
          : items.map((it) => {
              if (!it.group) return <Leaf key={it.to} it={it} />;
              const isOpen = !!openGroups[it.group];
              const active = childActive(it.children, location.pathname);
              const showGroupBadge = role === "admin"
                && it.children.some((c) => c.to === "/billing/payments")
                && pendingPayments > 0;
              return (
                <div key={it.group}>
                  <button
                    type="button"
                    data-testid={`nav-group-${slug(it.group)}`}
                    onClick={() => setOpenGroups((p) => ({ ...p, [it.group]: !p[it.group] }))}
                    className={cn(
                      "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200",
                      active && !isOpen
                        ? "text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <it.icon className="w-5 h-5 shrink-0" weight="duotone" />
                    <span className="truncate">{it.group}</span>
                    {showGroupBadge && (
                      <span
                        data-testid="pending-payments-badge-group"
                        className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-bold"
                      >
                        {pendingPayments}
                      </span>
                    )}
                    <CaretDown className={cn("w-4 h-4 transition-transform duration-200", showGroupBadge ? "ml-1.5" : "ml-auto", isOpen && "rotate-180")} />
                  </button>
                  {isOpen && (
                    <div className="mt-1 space-y-1">
                      {it.children.map((c) => (
                        <Leaf key={c.to} it={c} nested />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
      </nav>

      <button
        onClick={onToggle}
        data-testid="sidebar-collapse-btn"
        className="h-12 border-t border-border flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
      >
        <CaretLeft className={cn("w-4 h-4 transition-transform duration-300", collapsed && "rotate-180")} />
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
}
